import AutocheckinController from "./autocheckin_controller"
import { loadYouTubeSDK } from "./concerns/youtube_sdk"
import { Timestampable } from "./concerns/timestampable"

// If initialTimestamp is present, the video will start at that timestamp.
export default class extends AutocheckinController {
  static targets = [ 'iframeContainer' ]

  static values = {
    initialTimestamp: String,
    inline: { type: Boolean, default: false }
  }

  static outlets = [ "post-open-session" ]

  // contador de cantidad de refresh que se van haciendo
  // timeupdate se dispara cada checkEveryMs (1s)
  timeUpdatesCount = 0
  checkEveryMs = 1000

  // solo hacer checkin después de 1min seguidos de reproducción
  //  si hacen "seeked" no se resetea la cuenta.
  checkinWaitTimeUpdatesCount = 60

  player = null

  initialize() {
    super.initialize()
    Timestampable(this)
  }

  connect(){
    this.connected = true
    loadYouTubeSDK().then(() => this.initPlayer()).catch((error) => this.log(error))
  }

  disconnect(){
    this.connected = false
    clearInterval(this.refreshIntervalId)
    if (this.player) {
      // YT.destroy removes the iframe; retain its markup for a Turbo reconnect.
      const iframe = this.iframe().cloneNode(true)
      this.player.destroy()
      this.iframeContainerTarget.append(iframe)
      this.player = null
    }
  }

  onYouTubeIframeAPIReady(){
    this.initPlayer()
  }

  initPlayer(){
    if (!this.connected || this.player || !window.YT?.Player) return
    this.player = new window.YT.Player(this.iframe(), {
      events: {
        // @see https://developers.google.com/youtube/iframe_api_reference#Events
        onReady: (event) => {
          if (this.connected) this.restorePosition()
        },
        onStateChange: (event) => {
          if (!this.connected) return
          clearInterval(this.refreshIntervalId)
          // event.data is -1:unstarted, 0:ended, 1:playing, 2:paused, 3:buffering, 5:video cued.
          if(event.data == 1) {
            if (this.hasPostOpenSessionOutlet) this.postOpenSessionOutlet.activate()
            this.updateSessionPosition()
            this.refreshIntervalId = setInterval(() => {
              this.timeUpdatesCount++
              this.updateSessionPosition()
              if(this.shouldSilentCheckin(event)){
                this.silentCheckin()
              }
            }, this.checkEveryMs)
          } else {
            clearInterval(this.refreshIntervalId)
            if (event.data == 2 && this.hasPostOpenSessionOutlet) {
              this.updateSessionPosition()
              this.postOpenSessionOutlet.updateServerWithCurrentPosition()
            }
            if(event.data == 0) {
              if(this.shouldCheckin(event)){
                this.inlineValue ? this.silentCheckin() : this.checkinAndRedirect()
              } else {
                if (this.hasPostOpenSessionOutlet){
                  this.postOpenSessionOutlet.destroySession()
                }
              }
            }
          }
        },
        onError: (event) => {
          // event.data is 2:invalid parameter, 100:video not found, 101:video not allowed in embedded players, 150:video not allowed in embedded players
        }
      }
    })
  }

  updateSessionPosition(){
    if (this.hasPostOpenSessionOutlet) {
      this.postOpenSessionOutlet.tickUpdateSession(this.timeUpdatesCount, {position: this.player.getCurrentTime()})
    }
  }

  aboveCheckinPercentageThreashold(event){
    let totalDuration = this.player.getDuration()
    let curPosition = this.player.getCurrentTime()
    let percentage = (curPosition/totalDuration)*100
    return percentage > this.data.get("percentageForAutoCheckin")
  }

  shouldSilentCheckin(event){
    return this.shouldCheckin(event) && this.aboveCheckinPercentageThreashold(event) && (this.timeUpdatesCount > this.checkinWaitTimeUpdatesCount)
  }

  togglePlay(keyEvent){
    this.log("toggle play")
    /* ignore keyEvent if its in a text input or textarea */
    if (this.inlineValue || ["INPUT", "TEXTAREA", "SELECT"].includes(keyEvent.target?.tagName) || keyEvent.target?.isContentEditable) {
      return
    }

    keyEvent.preventDefault()

    if (this.player) {
      if(this.player.getPlayerState() == 1){
        this.player.pauseVideo()
      } else {
        this.player.playVideo()
      }
    }
  }

  restorePosition(){
    if (this.hasInitialTimestampValue && this.isTimestamp(this.initialTimestampValue)){
      this.setPosition(this.timestampToSeconds(this.initialTimestampValue))
    }
  }

  setPosition(seconds){
    this.log("setting position: "+seconds)
    if (this.player) this.player.seekTo(seconds, true)
  }

  iframe(){
    return this.iframeContainerTarget.querySelector("iframe")
  }

}
